import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import Button from '@/common/button';
import { notify } from '@/components/ui/notify';
import axiosInstance from '@/configs/axios';
import type { CustomTrial } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';
import { openAiAdsClickRefBody } from '@/utils/openai-ads-click-ref';
import { reportOpenAiAdsConversion } from '@/utils/openai-ads-conversions';

interface AlreadyLoggedInNoticeProps {
  trialConfigId?: number | null;
  trialPageId?: number | null;
  // Optional, and only trial_beta passes it: that LP's post-signup screen is the beta installer,
  // not the dashboard link this component renders by default. Without it the six other LPs keep
  // the `started` screen they have today.
  onTrialStarted?: () => void;
}

type EligibilityStatus = 'idle' | 'checking' | 'eligible' | 'ineligible' | 'starting' | 'started' | 'error';

// Any reason outside this list falls back to the generic `default` copy: the slugs come from the API
// and a new one would otherwise render its own name on screen until 10 locale files caught up.
const KNOWN_INELIGIBILITY_REASONS = ['already_has_trial', 'active_subscription', 'active_vip', 'invalid_trial_config'];

// Shown instead of the create-account form on a custom-trial LP when the visitor is already
// authenticated (see src/components/trial/trial-layout-switcher.tsx). SCRUM-1663 added
// GET /user/trial-eligibility + POST /user/trial/start so a logged-in visitor can start the SAME
// custom trial from their existing account (no new registration) when eligible — mirroring the
// eligibility rules the backend already enforces (one trial per account, no active subscription,
// no active VIP, valid trial_config).
export function AlreadyLoggedInNotice({ trialConfigId, trialPageId, onTrialStarted }: AlreadyLoggedInNoticeProps = {}) {
  const { t } = useTranslation('common');
  const { user, logout, me } = useAuth();
  const [status, setStatus] = useState<EligibilityStatus>(trialConfigId ? 'checking' : 'idle');
  const [reason, setReason] = useState<string | null>(null);
  const [startedTrial, setStartedTrial] = useState<CustomTrial | null>(null);

  useEffect(() => {
    // `trialConfigId` is required by both endpoints, so with no config id there is nothing to check
    // and nothing to start — the component degrades to the plain dashboard/logout notice.
    if (!trialConfigId) {
      setStatus('idle');
      return undefined;
    }

    let isMounted = true;
    setStatus('checking');

    axiosInstance
      .get('/user/trial-eligibility', { params: { trialConfigId } })
      .then((response) => {
        if (!isMounted) return;

        const eligibility = response.data?.data ?? {};

        setReason(eligibility.reason ?? null);
        setStatus(eligibility.eligible ? 'eligible' : 'ineligible');
      })
      .catch(() => {
        if (!isMounted) return;
        setStatus('error');
      });

    return () => {
      isMounted = false;
    };
  }, [trialConfigId]);

  const handleStartTrial = async () => {
    if (!trialConfigId) return;

    setStatus('starting');

    try {
      const response = await axiosInstance.post('/user/trial/start', { ...openAiAdsClickRefBody(), trialConfigId, trialPageId: trialPageId ?? undefined });
      setStartedTrial(response.data?.data?.custom_trial ?? null);
      setStatus('started');
      // The second way a trial is granted in this front end — `handleCreateTrial` covers the signup
      // form, this covers the visitor who was already logged in. Both report the same conversion, and
      // the guard inside `reportOpenAiAdsConversion` keeps a visitor who does both from counting twice.
      reportOpenAiAdsConversion({ type: 'trial_started', planId: trialConfigId, email: user?.email ?? null });
      notify.success(t('login.trial_already_logged_in.start_success_title'));
      me();
      // After me(), so a caller that swaps this component out for its own post-signup screen does
      // it with the refreshed user in hand.
      if (onTrialStarted) onTrialStarted();
    } catch {
      // Back to `eligible`, never to a dead end: the click can fail for a reason that is gone on the
      // next try (network, a config that just expired), so the button stays available and the toast
      // is the only thing the visitor has to dismiss.
      setStatus('eligible');
      notify.error(t('login.trial_already_logged_in.start_error'));
    }
  };

  if (status === 'checking') {
    return (
      <div className='flex w-full flex-col items-center gap-4 rounded-xl border border-[#879BAB] bg-[#0E1113] p-6 text-center'>
        <p className='text-sm text-[#879BAB]'>{t('login.trial_already_logged_in.checking_eligibility')}</p>
      </div>
    );
  }

  if (status === 'started') {
    return (
      <div className='flex w-full flex-col items-center gap-4 rounded-xl border border-[#879BAB] bg-[#0E1113] p-6 text-center'>
        <h3 className='text-xl font-bold text-white'>{t('login.trial_already_logged_in.start_success_title')}</h3>
        <p className='text-sm text-[#879BAB]'>{t('login.trial_already_logged_in.start_success_description', { count: startedTrial?.trial_days ?? 0 })}</p>
        <Button as='link' href='/dashboard/overview' variant='primary' fullWidth>
          {t('login.trial_already_logged_in.button_dashboard')}
        </Button>
      </div>
    );
  }

  if (status === 'eligible' || status === 'starting') {
    return (
      <div className='flex w-full flex-col items-center gap-4 rounded-xl border border-[#879BAB] bg-[#0E1113] p-6 text-center'>
        <h3 className='text-xl font-bold text-white'>{t('login.trial_already_logged_in.title')}</h3>
        <p className='text-sm text-[#879BAB]'>{t('login.trial_already_logged_in.eligible_description', { email: user?.email ?? '' })}</p>
        <div className='flex w-full flex-col gap-3 sm:flex-row sm:justify-center'>
          <Button type='button' variant='primary' fullWidth loading={status === 'starting'} disabled={status === 'starting'} onClick={handleStartTrial}>
            {t('login.trial_already_logged_in.start_button')}
          </Button>
          <Button type='button' variant='gray' fullWidth onClick={() => logout()}>
            {t('login.trial_already_logged_in.button_logout')}
          </Button>
        </div>
      </div>
    );
  }

  // idle (no trialConfigId to check) | ineligible | error (treated as ineligible — never block the
  // existing dashboard/logout notice on an eligibility-check failure)
  const reasonKey = reason && KNOWN_INELIGIBILITY_REASONS.includes(reason) ? reason : 'default';

  return (
    <div className='flex w-full flex-col items-center gap-4 rounded-xl border border-[#879BAB] bg-[#0E1113] p-6 text-center'>
      <h3 className='text-xl font-bold text-white'>{t('login.trial_already_logged_in.title')}</h3>
      <p className='text-sm text-[#879BAB]'>{t('login.trial_already_logged_in.description', { email: user?.email ?? '' })}</p>
      {status === 'ineligible' && <p className='text-xs text-[#5C6B78]'>{t(`login.trial_already_logged_in.reason.${reasonKey}`)}</p>}
      <div className='flex w-full flex-col gap-3 sm:flex-row sm:justify-center'>
        <Button as='link' href='/dashboard/overview' variant='primary' fullWidth>
          {t('login.trial_already_logged_in.button_dashboard')}
        </Button>
        <Button type='button' variant='gray' fullWidth onClick={() => logout()}>
          {t('login.trial_already_logged_in.button_logout')}
        </Button>
      </div>
    </div>
  );
}
